import React from 'react';

import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EditTwoTone,
  EyeTwoTone,
  FileDoneOutlined,
  ReconciliationOutlined,
  RightCircleTwoTone,
  StopTwoTone,
  ToolOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import _ from 'lodash';
import { getIntl } from 'umi';

import { fieldConfigurationMapping } from '@/utils/core/business-logic';
import { prepareEditFieldValue } from '@/utils/core/business-logic/field-configuration';
import { CoreEnums } from '@/utils/domain/constants';
import { sortFn } from '@/utils/infrastructure';

import ColumnHeader from '../byList/components/ColumnHeader';
import { iconForField } from '../byList/fieldIcons';
import { normalizeCustomCommandType, normalizeRequestCommand } from '../byList/utils';

const toSorted = (arr = []) => {
  return arr.sort(sortFn);
};

const {
  CustomListCommandStringType,
  CustomListCommandType,
  CustomListFieldType,
  CustomListFieldStringType,
  CustomListType,
  CustomListStringType,
  EntityQueryOperationType,
  FormFieldType,
  FormFieldStringType,
} = CoreEnums;

const fileTypes = [
  FormFieldStringType.Attachment,
  FormFieldStringType.Image,
  FormFieldStringType.Photo,

  FormFieldType.Attachment,
  FormFieldType.Image,
  FormFieldType.Photo,
];

export const defaultHiddenCommands = {
  [CustomListCommandType.MassRequestsApprove]: true,
  [CustomListCommandType.MassRequestsReject]: true,
  [CustomListCommandType.MassRequestsCancel]: true,
  [CustomListCommandType.MassRequestsVoid]: true,
  [CustomListCommandStringType.MassRequestsApprove]: true,
  [CustomListCommandStringType.MassRequestsReject]: true,
  [CustomListCommandStringType.MassRequestsCancel]: true,
  [CustomListCommandStringType.MassRequestsVoid]: true,
};

export const commandButtonType = {
  [CustomListCommandType.MassRequestsApprove]: 'success',
  [CustomListCommandType.MassRequestsReject]: 'danger',
  [CustomListCommandType.MassRequestsCancel]: 'danger',
  [CustomListCommandType.MassRequestsVoid]: 'danger',
  [CustomListCommandStringType.MassRequestsApprove]: 'success',
  [CustomListCommandStringType.MassRequestsReject]: 'danger',
  [CustomListCommandStringType.MassRequestsCancel]: 'danger',
  [CustomListCommandStringType.MassRequestsVoid]: 'danger',
};

export const localCustomListCommands = [
  {
    id: 'rc-btn-reject',
    command: CustomListCommandType.RejectRequest,
    commandText: 'Rechazar Solicitud',
    route: 'processEngine/reject/{processInstanceId}/{actionInstanceId}',
    httpMethod: 'POST',
  },
  {
    id: 'rc-btn-massReject',
    command: CustomListCommandType.MassRequestsReject,
    commandText: 'Rechazar Solicitud',
    route: '',
    httpMethod: 'POST',
  },
];

export const localMapListCommands = [
  {
    id: 'rc-btn-approve',
    command: CustomListCommandType.ApproveRequest,
    commandText: 'Aprobar Solicitud',
    route: 'processEngine/approve/{processInstanceId}/{actionInstanceId}',
    httpMethod: 'POST',
  },
  {
    id: 'rc-btn-cancel',
    command: CustomListCommandType.CancelRequest,
    commandText: 'Cancelar Solicitud',
    route: 'processEngine/cancel/{processInstanceId}',
    httpMethod: 'POST',
  },
];

export const thCmdKeys = [
  CustomListCommandType.EntityAdd,
  CustomListCommandType.Export,
  CustomListCommandType.CreateRequest,
  CustomListCommandType.MassRequestsApprove,
  CustomListCommandType.MassRequestsReject,
  CustomListCommandType.MassRequestsCancel,
  CustomListCommandType.MassRequestsVoid,
  CustomListCommandStringType.EntityAdd,
  CustomListCommandStringType.Export,
  CustomListCommandStringType.CreateRequest,
  CustomListCommandStringType.MassRequestsApprove,
  CustomListCommandStringType.MassRequestsReject,
  CustomListCommandStringType.MassRequestsCancel,
  CustomListCommandStringType.MassRequestsVoid,
];

export const trCmdKeys = [
  CustomListCommandType.ViewDetails,
  CustomListCommandType.CancelRequest,
  CustomListCommandType.VoidRequest,
  CustomListCommandType.CompleteRequestInformation,
  CustomListCommandType.ApproveRequest,
  CustomListCommandType.RejectRequest,
  CustomListCommandType.ExecuteTask,
  CustomListCommandType.SignDocument,
  CustomListCommandType.ReassignTask,
  CustomListCommandStringType.ViewDetails,
  CustomListCommandStringType.CancelRequest,
  CustomListCommandStringType.VoidRequest,
  CustomListCommandStringType.CompleteRequestInformation,
  CustomListCommandStringType.ApproveRequest,
  CustomListCommandStringType.RejectRequest,
  CustomListCommandStringType.ExecuteTask,
  CustomListCommandStringType.SignDocument,
  CustomListCommandStringType.ReassignTask,
];

export const commandByKey = _.toPairs(CustomListCommandType).reduce(
  (lhs, [key, value]) => ({ ...lhs, [value]: key }),
  {},
);

export const renderCommandIcon = (command) => {
  return {
    [CustomListCommandType.ViewDetails]: <EyeTwoTone />,
    [CustomListCommandType.CancelRequest]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandType.VoidRequest]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandType.CompleteRequestInformation]: (
      <CheckCircleTwoTone twoToneColor="#0dc26b" />
    ),
    [CustomListCommandType.ApproveRequest]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CustomListCommandType.RejectRequest]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandType.ExecuteTask]: <ToolOutlined />,
    [CustomListCommandType.SignDocument]: <FileDoneOutlined />,
    [CustomListCommandType.ReassignTask]: <ReconciliationOutlined />,
    [CustomListCommandType.MassRequestsApprove]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CustomListCommandType.MassRequestsReject]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandType.MassRequestsCancel]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandType.MassRequestsVoid]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandStringType.ViewDetails]: <EyeTwoTone />,
    [CustomListCommandStringType.CancelRequest]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandStringType.VoidRequest]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandStringType.CompleteRequestInformation]: (
      <CheckCircleTwoTone twoToneColor="#0dc26b" />
    ),
    [CustomListCommandStringType.ApproveRequest]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CustomListCommandStringType.RejectRequest]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandStringType.ExecuteTask]: <ToolOutlined />,
    [CustomListCommandStringType.SignDocument]: <FileDoneOutlined />,
    [CustomListCommandStringType.ReassignTask]: <ReconciliationOutlined />,
    [CustomListCommandStringType.MassRequestsApprove]: (
      <CheckCircleTwoTone twoToneColor="#0dc26b" />
    ),
    [CustomListCommandStringType.MassRequestsReject]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandStringType.MassRequestsCancel]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandStringType.MassRequestsVoid]: <StopTwoTone twoToneColor="#ff5b70" />,
  }[command];
};

export const renderRequestCmdIcon = (cmd) => {
  const command = normalizeRequestCommand(cmd);
  const customCommandType = normalizeCustomCommandType(cmd);

  return {
    [CoreEnums.RequestDetailCommandType.Approve]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CoreEnums.RequestDetailCommandType.Reject]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
    [CoreEnums.RequestDetailCommandType.Complete]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CoreEnums.RequestDetailCommandType.Cancel]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CoreEnums.RequestDetailCommandType.Void]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CoreEnums.RequestDetailCommandType.Details]: <EyeTwoTone />,
    [CoreEnums.RequestDetailCommandType.Continue]: <EditTwoTone />,
    [CoreEnums.RequestDetailCommandType.ExecuteTask]: <ToolOutlined />,
    [CoreEnums.RequestDetailCommandType.Sign]: <FileDoneOutlined />,
    [CoreEnums.RequestDetailCommandType.ReassignTask]: (
      <UserSwitchOutlined style={{ color: 'var(--ant-primary-color)' }} />
    ),
    [CoreEnums.RequestDetailCommandType.GoToActionFromCustomCommand]: {
      [CoreEnums.ActionCustomCommandType.Approve]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
      [CoreEnums.ActionCustomCommandType.Reject]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
      [CoreEnums.ActionCustomCommandType.Skip]: <RightCircleTwoTone twoToneColor="#faad14" />,
    }[customCommandType],
  }[command];
};

const ROW_LABEL_KEYS = {
  [CoreEnums.RequestDetailCommandType.Details]: 'customList.text.detail',
  [CoreEnums.RequestDetailCommandType.Approve]: 'customList.text.approve',
  [CoreEnums.RequestDetailCommandType.Reject]: 'customList.text.reject',
  [CoreEnums.RequestDetailCommandType.Complete]: 'customList.text.complete',
  [CoreEnums.RequestDetailCommandType.ExecuteTask]: 'customList.text.execute',
  [CoreEnums.RequestDetailCommandType.Sign]: 'customList.text.sign',
  [CoreEnums.RequestDetailCommandType.Cancel]: 'customList.text.cancel',
  [CoreEnums.RequestDetailCommandType.Void]: 'customList.text.void',
  [CoreEnums.RequestDetailCommandType.ReassignTask]: 'customList.text.reassign',
};

const HEADER_LABEL_KEYS = {
  [CustomListCommandType.ViewDetails]: 'customList.text.detail',
  [CustomListCommandType.CancelRequest]: 'customList.text.cancel',
  [CustomListCommandType.VoidRequest]: 'customList.text.void',
  [CustomListCommandType.CompleteRequestInformation]: 'customList.text.complete',
  [CustomListCommandType.ApproveRequest]: 'customList.text.approve',
  [CustomListCommandType.RejectRequest]: 'customList.text.reject',
  [CustomListCommandType.ExecuteTask]: 'customList.text.execute',
  [CustomListCommandType.SignDocument]: 'customList.text.sign',
  [CustomListCommandType.ReassignTask]: 'customList.text.reassign',
  [CustomListCommandType.MassRequestsApprove]: 'customList.text.approve',
  [CustomListCommandType.MassRequestsReject]: 'customList.text.reject',
  [CustomListCommandType.MassRequestsCancel]: 'customList.text.cancel',
  [CustomListCommandType.MassRequestsVoid]: 'customList.text.void',
  [CustomListCommandStringType.ViewDetails]: 'customList.text.detail',
  [CustomListCommandStringType.CancelRequest]: 'customList.text.cancel',
  [CustomListCommandStringType.VoidRequest]: 'customList.text.void',
  [CustomListCommandStringType.CompleteRequestInformation]: 'customList.text.complete',
  [CustomListCommandStringType.ApproveRequest]: 'customList.text.approve',
  [CustomListCommandStringType.RejectRequest]: 'customList.text.reject',
  [CustomListCommandStringType.ExecuteTask]: 'customList.text.execute',
  [CustomListCommandStringType.SignDocument]: 'customList.text.sign',
  [CustomListCommandStringType.ReassignTask]: 'customList.text.reassign',
  [CustomListCommandStringType.MassRequestsApprove]: 'customList.text.approve',
  [CustomListCommandStringType.MassRequestsReject]: 'customList.text.reject',
  [CustomListCommandStringType.MassRequestsCancel]: 'customList.text.cancel',
  [CustomListCommandStringType.MassRequestsVoid]: 'customList.text.void',
};

export const renderCommandLabel = (cmd, isRow = false) => {
  if (!cmd) return '';
  const id = isRow
    ? ROW_LABEL_KEYS[normalizeRequestCommand(cmd)] ?? HEADER_LABEL_KEYS[cmd.command]
    : HEADER_LABEL_KEYS[cmd.command] ?? ROW_LABEL_KEYS[normalizeRequestCommand(cmd)];

  return id ? getIntl().formatMessage({ id }) : cmd.commandText;
};

const ROW_RESPONSE_MESSAGES = {
  [CoreEnums.RequestDetailCommandType.Approve]: 'custom.lists.text.message.approve',
  [CoreEnums.RequestDetailCommandType.Reject]: 'custom.lists.text.message.reject',
  [CoreEnums.RequestDetailCommandType.Complete]: 'custom.lists.text.message.complete',
  [CoreEnums.RequestDetailCommandType.ExecuteTask]: 'custom.lists.text.message.execute',
  [CoreEnums.RequestDetailCommandType.Sign]: 'custom.lists.text.message.sign',
  [CoreEnums.RequestDetailCommandType.Cancel]: 'custom.lists.text.message.cancel',
  [CoreEnums.RequestDetailCommandType.Void]: 'custom.lists.text.message.void',
  [CoreEnums.RequestDetailCommandType.ReassignTask]: 'custom.lists.text.message.reassign',
  [CoreEnums.RequestDetailCommandType.GoToActionFromCustomCommand]:
    'custom.lists.text.message.execute',
};

const HEADER_RESPONSE_MESSAGES = {
  [CustomListCommandType.CancelRequest]: 'custom.lists.text.message.cancel',
  [CustomListCommandType.VoidRequest]: 'custom.lists.text.message.void',
  [CustomListCommandType.ApproveRequest]: 'custom.lists.text.message.approve',
  [CustomListCommandType.ExecuteTask]: 'custom.lists.text.message.execute',
  [CustomListCommandType.SignDocument]: 'custom.lists.text.message.sign',
  [CustomListCommandType.ReassignTask]: 'custom.lists.text.message.reassign',
  [CustomListCommandType.RejectRequest]: 'custom.lists.text.message.reject',
  [CustomListCommandType.MassRequestsApprove]: 'custom.lists.text.message.approve',
  [CustomListCommandType.MassRequestsReject]: 'custom.lists.text.message.reject',
  [CustomListCommandType.MassRequestsCancel]: 'custom.lists.text.message.cancel',
  [CustomListCommandType.MassRequestsVoid]: 'custom.lists.text.message.void',
  [CustomListCommandStringType.CancelRequest]: 'custom.lists.text.message.cancel',
  [CustomListCommandStringType.VoidRequest]: 'custom.lists.text.message.void',
  [CustomListCommandStringType.ApproveRequest]: 'custom.lists.text.message.approve',
  [CustomListCommandStringType.ExecuteTask]: 'custom.lists.text.message.execute',
  [CustomListCommandStringType.SignDocument]: 'custom.lists.text.message.sign',
  [CustomListCommandStringType.ReassignTask]: 'custom.lists.text.message.reassign',
  [CustomListCommandStringType.RejectRequest]: 'custom.lists.text.message.reject',
  [CustomListCommandStringType.MassRequestsApprove]: 'custom.lists.text.message.approve',
  [CustomListCommandStringType.MassRequestsReject]: 'custom.lists.text.message.reject',
  [CustomListCommandStringType.MassRequestsCancel]: 'custom.lists.text.message.cancel',
  [CustomListCommandStringType.MassRequestsVoid]: 'custom.lists.text.message.void',
};

export const commandResponseMessage = (command, isRow = false) => {
  if (isRow) return ROW_RESPONSE_MESSAGES[command] ?? HEADER_RESPONSE_MESSAGES[command];
  return HEADER_RESPONSE_MESSAGES[command] ?? ROW_RESPONSE_MESSAGES[command];
};

/**
 * Backend ships dynamic-entity columns in two layers:
 *
 *   - `field.dynamicEntityField` carries an *abridged* descriptor (just
 *     `id`, `name`, `internalName`, `type`) — enough to render a value
 *     but not enough to drive an editor (no `required`,
 *     `defaultValue`, `regularExpression`, `formula`, `allowMultiple`,
 *     `columnWidth`, `observations`, …).
 *   - `entity.fields[]` carries the *full* form-builder definition
 *     (same `id`s).
 *
 * Inline edit, validation, and form-builder dependent renderings
 * (e.g. attachment with `allowMultiple`) all rely on the full
 * definition. We hydrate `dynamicEntityField` with the matching
 * `entity.fields[]` entry once, at config-load time, so every
 * downstream consumer (column mapper, EditableCell,
 * fieldConfigurationMapping, CardItem, …) reads the same shape.
 *
 * We also stamp the owning `entityId` on every hydrated field. Some
 * field types (e.g. `selector-user`, `selector-position`) call the
 * backend `field/getData/{id}/{op}` endpoint which requires
 * `entityId` whenever the field belongs to an entity. The CustomList
 * page lives at `/custom-lists/view/...` so the URL-based detection
 * inside `SelectorFieldHandler` doesn't fire — passing it explicitly
 * here is the canonical fix.
 *
 * Skipped when there's no entity fields list (dataOrigin 1, process
 * fields), and resilient to id-mismatches (falls back to the original
 * abridged descriptor).
 *
 * @param {Object[]} fields - CustomList fields from `payload.config.fields`
 * @param {Object[]} [entityFields] - `payload.config.entity?.fields`
 * @param {string}   [entityId] - `payload.config.dynamicEntityId`
 * @returns {Object[]} same `fields` shape with enriched `dynamicEntityField`
 */
export function hydrateEntityFieldDefinitions(fields = [], entityFields = [], entityId = null) {
  const hasEntityFields = Array.isArray(entityFields) && entityFields.length > 0;
  if (!hasEntityFields && !entityId) return fields;
  const byId = hasEntityFields ? new Map(entityFields.map((f) => [f?.id, f])) : null;
  return fields.map((field) => {
    if (!field?.dynamicEntityField) return field;
    const def = byId?.get(field.dynamicEntityField.id);
    // Full entity-field definition first; abridged descriptor wins on
    // overlapping keys so any explicit overrides on the list config
    // (rare, but legal) survive the merge. `entityId` is stamped last
    // so it's always present even when no `def` was matched.
    return {
      ...field,
      dynamicEntityField: {
        ...(def ?? {}),
        ...field.dynamicEntityField,
        ...(entityId && { entityId }),
      },
    };
  });
}

export function fieldAndColumnMapper(fields = [], listId = null) {
  const formFields = [];
  const filtersConfig = [];
  let withEditableFields = false;

  const columns = toSorted(fields).map((item) => {
    const chunk = _.chunk(item.displayText, 3);

    const base = {
      id: item.id,
      name: item.dynamicEntityField?.internalName || item.internalName,
      label: item.displayText,
    };

    // `formFields` feeds the AddForm drawer. Only entity fields the
    // list owner explicitly opted into editing (`editEnabled: true`)
    // should be writable from the create flow — others (e.g. system
    // identifiers, autogenerated dates) stay out so users don't have
    // to enter values the backend will overwrite anyway. Inline edit
    // in the table reads `dynamicEntityField` straight from each
    // column descriptor, so this filter doesn't affect that path.
    if (
      (item?.type === CustomListFieldType.EntityField ||
        item?.type === CustomListFieldStringType.EntityField) &&
      item?.editEnabled
    ) {
      const { dynamicEntityField, ...more } = item ?? {};
      formFields.push({
        ...base,
        ...fieldConfigurationMapping({ ...more, ...dynamicEntityField }, fields),
      });
    }

    if (item.filterEnabled) {
      const knownCustomListFieldType = {
        [CustomListFieldType.InstanceCreated]: FormFieldType.Date,
        [CustomListFieldType.ApprovedDate]: FormFieldType.Date,
        [CustomListFieldType.RejectedDate]: FormFieldType.Date,
        [CustomListFieldType.EntityItemCreated]: FormFieldType.Date,

        [CustomListFieldStringType.InstanceCreated]: FormFieldType.Date,
        [CustomListFieldStringType.ApprovedDate]: FormFieldType.Date,
        [CustomListFieldStringType.RejectedDate]: FormFieldType.Date,
        [CustomListFieldStringType.EntityItemCreated]: FormFieldType.Date,
      };

      const isEntityDateField =
        (item?.type === CustomListFieldType.EntityField ||
          item?.type === CustomListFieldStringType.EntityField) &&
        item.dynamicEntityField?.type === FormFieldType.Date;

      const isCustomListField =
        item?.type === CustomListFieldType.Field ||
        item?.type === CustomListFieldStringType.Field ||
        !isEntityDateField;

      const fieldConfig = {
        ...(item.dynamicEntityField || item || {}),
        id: item.id,
        operationType: EntityQueryOperationType.ListFilter,
        type: knownCustomListFieldType[item?.type] || FormFieldType.DataBaseRemoteList,
        ...(isEntityDateField && { type: FormFieldType.Date }),
        ...(isCustomListField && { type: FormFieldType.DataBaseRemoteList }),
      };

      const configMapping = fieldConfigurationMapping(fieldConfig, fields, false);
      filtersConfig.push({
        ...base,
        ...configMapping,
        ...(configMapping.nomenclature &&
          listId && { params: { ...configMapping.params, listId } }),
      });
    }

    const isEditable = !!(
      (item?.type === CustomListFieldType.EntityField ||
        item?.type === CustomListFieldStringType.EntityField) &&
      item.editEnabled
    );

    if (!withEditableFields && isEditable) withEditableFields = isEditable;

    // Form-builder data type (`text`, `boolean`, `attachment`, …).
    // - `dynamicEntityField.type`: entity-backed lists (dataOrigin 2).
    // - `field.type`: process-form lists (dataOrigin 1).
    // - `item.type`: reserved CustomList cell types
    //   (`applicant-email`, `instance-status`, …) — cascade fallback.
    //
    // We propagate this on the column descriptor as `type` so the
    // generic table renderer (`renderColumnsMapping`) and the
    // EditableCell pick the right widget without each one duplicating
    // the resolution logic. The original CustomList cellType is kept
    // around as `cellType` for components that still key off it
    // (icons, header decorations…).
    const dataType = item?.dynamicEntityField?.type ?? item?.field?.type ?? item?.type;

    // Resolve the field icon through the shared cascade so each
    // column header carries the same pictogram its mosaic CardItem
    // would.
    const Icon = iconForField({
      fieldType: item?.dynamicEntityField?.type || item?.field?.type,
      cellType: item?.type,
    });

    // Column width budget. The legacy formula (`chunk.length * 36` /
    // 150 floor) didn't reserve room for the leading <ColumnHeader>
    // icon + sort caret, which made multi-word titles wrap to two
    // lines (e.g. "NOMBRE COMPLETO"). We bump both the per-chunk
    // multiplier and the floor by ~20px to fit "icon + label + sort
    // caret" comfortably, and rely on `onHeaderCell.whiteSpace: nowrap`
    // (paired with the table's `scroll: { x: 'max-content' }`) as a
    // safety net so the column auto-grows if the heuristic still
    // underestimates a particular label.
    const calcWidth = chunk.length > 3 ? chunk.length * 40 : 170;

    return {
      isEditable,
      key: item?.internalName,
      title: React.createElement(ColumnHeader, { icon: Icon, label: item?.displayText }),
      dataIndex: item?.internalName,
      type: dataType,
      cellType: item?.type,
      width: calcWidth,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...(isEditable && { dynamicEntityField: item.dynamicEntityField }),
      ...(fileTypes.includes(dataType) && { width: 170 }),
      sorter: true,
    };
  });

  return { formFields, filtersConfig, columns, withEditableFields };
}

/**
 * Build the `initialValues` payload that pre-fills the AddForm
 * drawer when it opens in edit mode (`formMode: "popup"`).
 *
 * Consumed by `<FormBuilder initialValues={...}>`, which forwards it
 * to Ant Design `<Form initialValues>`. The drawer is mounted with
 * `destroyOnClose`, so each open is a fresh mount and Antd applies
 * the snapshot once at render time — exactly the same path the
 * entity-manager `AddNewItem` uses.
 *
 * Why this isn't a one-liner:
 *
 *  1. The record returned by the list query keys values by the
 *     column `dataIndex` (= `item.internalName`, the CustomListField
 *     name), while the FormItem in the drawer expects them under the
 *     entity field name (`item.dynamicEntityField.internalName`).
 *     When those two differ — common for entity-backed lists, e.g.
 *     column "Correo" wired to entity field "Email" — a naive
 *     `_.pick` by form-field name silently misses the value. We
 *     bridge the two by walking the original `fields` list (which
 *     carries both names per item).
 *
 *  2. Some types need shape correction before reaching FormBuilder
 *     (booleans → `bool`, selectors → `[{label,value}]`, attachments
 *     → parsed JSON). We delegate to `prepareEditFieldValue`, the
 *     same helper used by the inline-edit path (`EditableCell`) and
 *     by the entity-manager `AddNewItem`, so all three edit surfaces
 *     stay byte-identical.
 *
 *  3. Keys whose final value is `null`/`""`/`undefined` are dropped
 *     from the payload entirely. Antd `<Form initialValues>` treats
 *     a missing key as "no value" (FormItem starts at `undefined`),
 *     while a present-but-`null` key would leak into widgets that
 *     interpret it — notably Antd `<Select mode="multiple"
 *     labelInValue>`, which normalises `null` to `[null]` and
 *     renders a ghost tag with `label:null,value:null` ("Usuario
 *     null" / "Puesto X"). Omitting the key keeps the FormItem in
 *     its true empty state.
 *
 * Plain text/number/choice values pass through unchanged.
 *
 * @param {Object} record   - The currently-selected row (raw from the API).
 * @param {Object[]} fields - `customListConfig.fields` (carries both names).
 * @returns {Object} ready-to-pass payload for `<Form initialValues={...}>`.
 */
export function buildEditInitialValues(record, fields = []) {
  if (!record) return {};

  // (1) Start from a shallow clone so widgets that key off the
  //     original `dataIndex` still find their value, then bridge any
  //     entity field whose form-name differs from its dataIndex.
  const initial = { ...record };
  fields.forEach((item) => {
    if (!item?.editEnabled) return;
    const formName = item?.dynamicEntityField?.internalName;
    const recordKey = item?.internalName;
    if (!formName || !recordKey || formName === recordKey) return;
    if (initial[formName] === undefined && record?.[recordKey] !== undefined) {
      initial[formName] = record[recordKey];
    }
  });

  // (2) Reshape each value the same way the entity manager does
  //     before feeding it to FormBuilder: boolean strings → real
  //     booleans, selector blobs → `[{label, value}]`, attachments
  //     → parsed JSON, etc. Plain text / numbers fall through.
  fields.forEach((item) => {
    const formName = item?.dynamicEntityField?.internalName ?? item?.internalName;
    if (!formName || initial[formName] === undefined) return;
    const fieldDef = item?.dynamicEntityField ?? item;
    initial[formName] = prepareEditFieldValue(fieldDef, initial[formName]);
  });

  // (3) Drop any key whose final value is "no value". See the JSDoc
  //     above for the rationale — leaving `null`/`""`/`undefined` in
  //     the payload would leak through `setFieldsValue` and produce
  //     ghost tags in selectors (e.g. "Usuario null" / "Puesto X").
  Object.keys(initial).forEach((k) => {
    const v = initial[k];
    if (v === null || v === undefined || v === '') delete initial[k];
  });

  return initial;
}

/**
 * Resolve the *effective* edit mode for a list configuration.
 *
 * Inline editing only makes sense for the table view (`type: List`)
 * where each cell can morph into a FormItem. Cards, maps, boards and
 * every future view render their items as static blocks, so for them
 * the only reasonable editor is the popup drawer regardless of what
 * the backend stores in `formMode`.
 *
 * Inside the table view we still let the config opt into inline
 * editing explicitly (`formMode: 'inline'`); anything else — popup,
 * an unknown value, or the absence of a value — defaults to popup so
 * attachments/photos/etc. are editable through the drawer by default.
 *
 * @param {Object} customListConfig - sub-object from the DVA state.
 * @returns {'inline' | 'popup'}
 */
export function effectiveFormMode(customListConfig) {
  if (!customListConfig) return 'popup';
  const isTableView =
    customListConfig.type === CustomListType.List ||
    customListConfig.type === CustomListStringType.List;
  return isTableView && customListConfig.formMode === 'inline' ? 'inline' : 'popup';
}

/**
 * Decide whether the row's "Edit" command should open the popup
 * drawer or fall back to the legacy inline-save dispatch.
 *
 * The Table component invokes `onEdit` with two distinct signatures
 * — `(record)` from the row menu and `(itemId, parsedDiff)` from
 * inline editing — so this helper detects which one we got and
 * routes accordingly. When the *effective* form mode is `'popup'`,
 * the row-menu signature opens the AddForm drawer pre-filled with
 * the record; for the inline signature (or any inline-mode list) we
 * forward to `editBy` so existing behaviour is preserved bit-for-bit.
 *
 * Returns the action object so callers can `dispatch(...)` it
 * directly without each page repeating the routing rules.
 *
 * @param {Object}  args
 * @param {*}       args.itemIdOrRecord   First arg from `onEdit`.
 * @param {*}       args.recordData       Second arg from `onEdit`.
 * @param {string=} args.formMode         Resolved mode (`'popup'` | `'inline'`).
 * @param {string}  args.listId           Current list id.
 * @returns {{ type: string, payload: any }} DVA action.
 */
export function buildEditAction({ itemIdOrRecord, recordData, formMode, listId }) {
  const isRecordPayload =
    recordData === undefined && typeof itemIdOrRecord === 'object' && itemIdOrRecord !== null;

  if (isRecordPayload && formMode === 'popup') {
    return {
      type: 'customListConfig/openEditPopup',
      payload: itemIdOrRecord,
    };
  }

  const itemId = isRecordPayload ? itemIdOrRecord?.Entity_Id ?? itemIdOrRecord?.id : itemIdOrRecord;
  const data = isRecordPayload ? itemIdOrRecord : recordData;

  return {
    type: 'customListConfig/editBy',
    payload: { listId, itemId, data },
  };
}
